import React from "react";
import { styled } from "@mui/material/styles";
import { OutlinedInput, Typography } from "@mui/material";
import { NODE_UI, stopPropagation } from "./nodeUiStyles";

/**
 * NodeTextField — plain MUI TextField restyled to match the NodeUI
 * shadcn-inspired look (compact, label above the input, muted borders).
 *
 * For arbitrary user text (names, descriptions, free-form values).
 * If the field is a workflow placeholder key, use NodePlaceholderTextField
 * instead — it adds ${...} adornments and sanitises the value.
 */

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  borderRadius: NODE_UI.borderRadiusSm,
  backgroundColor: theme.palette.background.default,
  "& fieldset": {
    borderColor: theme.palette.divider,
  },
  "&:hover fieldset": {
    borderColor: theme.palette.text.secondary,
  },
  "&.Mui-focused fieldset": {
    borderColor: theme.palette.text.primary,
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  "&.MuiInputBase-multiline": {
    padding: "0",
  },
  "& .MuiInputBase-inputMultiline": {
    padding: NODE_UI.input.padding,
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: "4px",
  userSelect: "none",
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.caption,
  lineHeight: 1.4,
  marginTop: "4px",
}));

const NodeTextField = React.memo(function NodeTextField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  fullWidth = true,
  multiline = false,
  minRows,
  maxRows,
  error = false,
  helperText,
  type = "text",
  inputProps,
  sx,
}) {
  const generatedId = React.useId();
  const inputId = inputProps?.id || `node-text-field-${generatedId}`;
  const labelId = `${inputId}-label`;
  const helperTextId = `${inputId}-helper`;
  const mergedInputProps = {
    ...inputProps,
    ...(label ? { "aria-labelledby": labelId } : {}),
    ...(helperText ? { "aria-describedby": helperTextId } : {}),
  };

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && (
        <Label id={labelId} component="label" htmlFor={inputId}>
          {label}
        </Label>
      )}
      <StyledOutlinedInput
        id={inputId}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        fullWidth={fullWidth}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        error={error}
        type={type}
        inputProps={mergedInputProps}
        size="small"
        sx={sx}
      />
      {helperText ? (
        <HelperText id={helperTextId}>{helperText}</HelperText>
      ) : null}
    </div>
  );
});

export default NodeTextField;
